// Sliders Components (swiper)
import { debounce } from "../tools/utils.js";
import Swiper from "swiper";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

const Slider = {};

Slider.initSwiper = (object, params = {}) => {
	return new Swiper(object, {
		modules: [Navigation, Pagination, Autoplay],
		loop: false,
		...params,
	});
};

// Fonction générique pour initialiser un slider en fonction du nombre de slides
// et de la largeur de l'écran
const initializeSlider = (sliderSelector, config) => {
	const swiperInstances = new Map();
	const bleedEnabled = config.bleed === true;

	// Fonction pour obtenir le slidesPerView et d'autres options
	const getSlidesPerView = () => {
		const width = window.innerWidth;

		// Obtenir les breakpoints triés dans l'ordre décroissant
		const breakpoints = Object.keys(config.breakpoints)
			.map(Number)
			.sort((a, b) => b - a);

		for (let bp of breakpoints) {
			if (width >= bp) {
				// Récupérer uniquement les options de ce breakpoint
				const breakpointConfig = config.breakpoints[bp];
				return breakpointConfig.slidesPerView;
			}
		}

		return 1;
	};

	const handleBleedAttribute = (sliderCtn) => {
		if (!bleedEnabled) return;

		// Vérifie si la classe 'no-swiper' est présente
		if (sliderCtn.classList.contains("no-swiper")) {
			// Si 'no-swiper' est présent, supprimer l'attribut 'data-bleed'
			sliderCtn.removeAttribute("data-bleed");
		} else {
			// Si 'no-swiper' n'est pas présent, ajouter l'attribut 'data-bleed'
			sliderCtn.setAttribute("data-bleed", "end");
		}
	};

	const maybeInitSlider = () => {
		const swiper = sliderSelector.querySelector(".swiper");
		if (!swiper) return;

		const parent = sliderSelector.parentElement;
		const slides = swiper.querySelectorAll(".swiper-slide");
		const slides_count = slides.length;
		const slidesPerView = getSlidesPerView();
		const minRequired = Math.ceil(slidesPerView);

		let existing_instance = swiperInstances.get(swiper);

		// console.debug(sliderSelector, slidesPerView, slides_count, minRequired);

		if (slides_count < minRequired) {
			if (sliderSelector.dataset.bleed) {
				sliderSelector.removeAttribute("data-bleed");
			}

			if (existing_instance) {
				existing_instance.destroy(true, true);
				swiperInstances.delete(swiper);
			}
			sliderSelector.classList.add("no-swiper");
			handleBleedAttribute(sliderSelector);
			return;
		}

		sliderSelector.classList.remove("no-swiper");
		handleBleedAttribute(sliderSelector);

		if (existing_instance) {
			existing_instance.update();
			return;
		}

		const baseOptions = {
			watchOverflow: true,
		};

		// Préparer l'option loop si fournie dans la config
		if (typeof config.loop === "boolean") {
			baseOptions.loop = config.loop;
		} else if (config.loop === true) {
			baseOptions.loop = true;
		}
		// Préparer les options d'autoplay si fournies dans la config
		const autoplayOptions = config.autoplay === true ? { delay: 5000, disableOnInteraction: false } : config.autoplay || null;

		const swiperOptions = {
			// Autoplay (sera pris en charge par Slider.initSwiper qui inclut le module)
			...(autoplayOptions ? { autoplay: autoplayOptions } : {}),
			pagination: {
				el: parent.querySelector(".swiper-pagination"),
				type: "bullets",
				clickable: true,
			},
			breakpoints: Object.keys(config.breakpoints).reduce((acc, bp) => {
				// Fusionner baseOptions dans chaque point de rupture
				acc[bp] = {
					...baseOptions, // Ajouter les options de base
					...config.breakpoints[bp], // Ajouter les options spécifiques à ce point de rupture
				};
				return acc;
			}, {}),
		};

		const instance = Slider.initSwiper(swiper, swiperOptions);
		swiperInstances.set(swiper, instance);
	};

	maybeInitSlider();
	const debouncedInitSwiper = debounce(maybeInitSlider, 200);
	window.addEventListener("resize", () => {
		debouncedInitSwiper();
	});
};

// ================================
// Slider Mobile - Générique
// ================================
const sliderMobile = (() => {
	const slidersData = new Map(); // Map<element, { swiper, originalChildren, options }>

	// Alias de breakpoints
	const breakpoints = {
		sm: "(max-width: 767.98px)",
		md: "(max-width: 991.98px)",
		lg: "(max-width: 1024px)",
	};

	// Valeur par défaut
	const defaultQuery = "(max-width: 767.98px)";

	const init = ({ selector, enableOn, ...userOptions }) => {
		let elements = [];

		if (typeof selector === "string") {
			elements = document.querySelectorAll(selector);
		} else if (selector instanceof HTMLElement) {
			elements = [selector];
		} else if (selector instanceof NodeList || Array.isArray(selector)) {
			elements = selector;
		}

		elements.forEach((el) => {
			if (!slidersData.has(el)) {
				slidersData.set(el, {
					swiper: null,
					originalChildren: Array.from(el.children),
					options: { enableOn, ...userOptions },
				});
			}
		});

		// Première exécution
		handleResize();

		// Resize listener
		window.addEventListener("resize", handleResize);
	};

	const handleResize = () => {
		slidersData.forEach((data, el) => {
			let query;

			// 1. Si on passe un alias ("sm", "md", "lg")
			if (data.options.enableOn && breakpoints[data.options.enableOn]) {
				query = breakpoints[data.options.enableOn];
			}
			// 2. Si on passe directement une media query custom ("(max-width: 900px)")
			else if (typeof data.options.enableOn === "string") {
				query = data.options.enableOn;
			}
			// 3. Sinon valeur par défaut
			else {
				query = defaultQuery;
			}

			const mq = window.matchMedia(query);

			if (mq.matches) {
				if (!data.swiper) {
					transformToSwiper(el, data);
				}
			} else {
				if (data.swiper) {
					restoreOriginal(el, data);
				}
			}
		});
	};

	const transformToSwiper = (el, data) => {
		el.classList.add("swiper");

		// Créer wrapper
		const wrapper = document.createElement("div");
		wrapper.classList.add("swiper-wrapper");

		data.originalChildren.forEach((child) => {
			child.classList.add("swiper-slide");
			wrapper.appendChild(child);
		});

		// Nettoyer & réinjecter
		el.innerHTML = "";
		el.appendChild(wrapper);

		let swiperOptions = {
			modules: [Pagination, Autoplay],
			slidesPerView: data.options.slidesPerView || 1,
			spaceBetween: data.options.spaceBetween || 0,
			slidesOffsetAfter: data.options.slidesOffsetAfter || 0,
			// Support des breakpoints Swiper
			...(data.options.breakpoints && { breakpoints: data.options.breakpoints }),
			...data.options,
		};

		// Pagination optionnelle
		if (data.options.pagination) {
			let pagination = el.querySelector(".swiper-pagination");
			if (!pagination) {
				pagination = document.createElement("div");
				pagination.classList.add("swiper-pagination");
				el.appendChild(pagination);
			}
			swiperOptions.pagination = {
				el: pagination,
				type: "bullets",
				clickable: true,
			};
		}

		// Init Swiper
		data.swiper = new Swiper(el, swiperOptions);
	};

	const restoreOriginal = (el, data) => {
		// Détruire Swiper
		data.swiper.destroy(true, true);
		data.swiper = null;

		// Restaurer HTML original
		el.classList.remove("swiper");
		el.innerHTML = "";
		data.originalChildren.forEach((child) => {
			child.classList.remove("swiper-slide");
			el.appendChild(child);
		});
	};

	return { init };
})();

export { Slider, sliderMobile, initializeSlider };
